import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Clock,
  BarChart3,
  CheckCircle,
  Upload,
  Link,
  Tag,
  MapPin,
  Phone,
  Mail,
  Globe,
  Building,
  User,
  Briefcase,
  PieChart,
  TrendingUp,
  Shield,
  Zap,
  Workflow,
  Layers,
  Database,
  Code,
  Star,
  Copy,
  Archive
} from 'lucide-react';
import { useToast } from '../common/Toast';
import MasterDataService from '../../services/masterDataService';
import SearchableDropdown from '../common/SearchableDropdown';
import API_CONFIG from '../../config/api';

const CreateContractType = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    description: '',
    category: '',
    type: '',
    status: 'active',
    priority: 'Medium',
    
    // Pricing Model Configuration
    pricingModel: 'Fixed-Price',
    baseAmount: '',
    currency: 'USD',
    hourlyRate: '',
    materialMarkup: '',
    billingFrequency: 'Monthly',
    
    // Fixed-Price Specific Configuration
    fixedPrice: {
      scope: [],
      deliverables: [],
      milestones: [],
      qualityGates: [],
      changeManagement: {
        enabled: true,
        approvalRequired: true,
        maxChanges: 5,
        changeFee: 0
      },
      performanceMetrics: {
        qualityScore: 0,
        timelinessScore: 0,
        customerSatisfaction: 0
      }
    },
    
    // Time & Materials Specific Configuration
    timeMaterials: {
      hourlyRates: [],
      materialCosts: [],
      timeTracking: {
        enabled: true,
        required: true,
        approvalRequired: true,
        maxHoursPerDay: 8,
        overtimeAllowed: false
      },
      costTracking: {
        enabled: true,
        categories: [],
        budgetLimits: [],
        costCeiling: 0
      },
      billingRules: {
        minimumHours: 0,
        roundingRules: 'quarter-hour',
        travelTime: false,
        administrativeTime: false
      }
    },
    
    // Contract Terms
    duration: '',
    autoRenewal: false,
    renewalTerms: '',
    noticePeriod: 30,
    terminationClause: '',
    
    // Approval & Compliance
    approvalLevel: 'Manager',
    requiresLegalReview: false,
    requiresFinanceReview: false,
    complianceRequirements: [],
    
    // Templates & Workflows
    defaultTemplate: '',
    workflowSteps: [],
    customFields: [],
    
    // Features & Capabilities
    features: [],
    integrations: [],
    reportingRequirements: [],
    
    // Advanced Settings
    tags: [],
    metadata: {},
    isPublic: false,
    allowCustomization: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [loadingComplianceTypes, setLoadingComplianceTypes] = useState(false);

  // Load compliance types when category changes
  useEffect(() => {
    const loadComplianceTypes = async () => {
      if (!formData.category) {
        setComplianceTypes([]);
        return;
      }

      setLoadingComplianceTypes(true);
      try {
        const response = await fetch(
          `${API_CONFIG.ENDPOINTS.COMPLIANCE_TYPES}?contractCategory=${encodeURIComponent(formData.category)}`,
          {
            headers: API_CONFIG.HEADERS
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setComplianceTypes(data.data || []);
          }
        }
      } catch (error) {
        console.error('Error loading compliance types:', error);
        setComplianceTypes([]);
      } finally {
        setLoadingComplianceTypes(false);
      }
    };

    loadComplianceTypes();
  }, [formData.category]);

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Basic Information',
      icon: <FileText size={18} />
    },
    {
      id: 'pricing',
      label: 'Pricing & Terms',
      icon: <DollarSign size={18} />
    },
    {
      id: 'workflow',
      label: 'Workflow & Templates',
      icon: <Workflow size={18} />
    },
    {
      id: 'features',
      label: 'Features & Integration',
      icon: <Zap size={18} />
    },
    {
      id: 'advanced',
      label: 'Advanced Settings',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value && value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  // Helper functions for nested object handling
  const handleNestedInputChange = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleArrayItemChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleNestedArrayItemChange = (field, index, subField, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [subField]: value } : item
      )
    }));
  };

  const handleWorkflowStepChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      workflowSteps: prev.workflowSteps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const handleAddWorkflowStep = () => {
    const newStep = {
      name: '',
      description: '',
      order: formData.workflowSteps.length + 1,
      required: true,
      approver: '',
      timeLimit: '',
      escalation: false
    };
    
    setFormData(prev => ({
      ...prev,
      workflowSteps: [...prev.workflowSteps, newStep]
    }));
  };

  const handleRemoveWorkflowStep = (index) => {
    setFormData(prev => ({
      ...prev,
      workflowSteps: prev.workflowSteps.filter((_, i) => i !== index)
    }));
  };

  const formatCode = (value) => {
    if (!value) return '';
    return value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const buildPayload = () => {
    const code = formatCode(formData.name) || `CONTRACTTYPE_${Date.now()}`;
    const metadata = {
      type: formData.type,
      category: formData.category,
      priority: formData.priority,
      pricingModel: formData.pricingModel,
      baseAmount: parseFloat(formData.baseAmount) || 0,
      currency: formData.currency,
      hourlyRate: parseFloat(formData.hourlyRate) || 0,
      materialMarkup: parseFloat(formData.materialMarkup) || 0,
      billingFrequency: formData.billingFrequency,
      fixedPrice: formData.fixedPrice,
      timeMaterials: formData.timeMaterials,
      duration: formData.duration,
      autoRenewal: formData.autoRenewal,
      renewalTerms: formData.renewalTerms,
      noticePeriod: parseInt(formData.noticePeriod, 10) || 30,
      terminationClause: formData.terminationClause,
      approvalLevel: formData.approvalLevel,
      requiresLegalReview: formData.requiresLegalReview,
      requiresFinanceReview: formData.requiresFinanceReview,
      complianceRequirements: formData.complianceRequirements,
      workflowSteps: formData.workflowSteps,
      features: formData.features,
      integrations: formData.integrations,
      reportingRequirements: formData.reportingRequirements,
      tags: formData.tags
    };

    return {
      category: 'ContractType',
      code,
      label: formData.name.trim(),
      description: formData.description.trim(),
      isActive: formData.status !== 'inactive',
      metadata
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showError('Contract type name is required');
      return;
    }
    // Prevent name starting with a number (can break routing/display)
    if (/^\d/.test(formData.name.trim())) {
      showError('Contract type name cannot start with a number. Please use a letter or word first.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Check for existing contract type with the same name
      const existingTypesResponse = await MasterDataService.getByCategory('ContractType');
      const existingTypes = existingTypesResponse?.data || [];
      
      const trimmedName = formData.name.trim();
      const duplicate = existingTypes.find(item => {
        const label = (item.label || '').trim();
        const code = (item.code || '').trim();
        return label.toLowerCase() === trimmedName.toLowerCase() || 
               code.toLowerCase() === trimmedName.toLowerCase();
      });
      
      if (duplicate) {
        showError(`A contract type with the name "${trimmedName}" already exists. Please use a different name.`);
        setIsSubmitting(false);
        return;
      }
      
      const payload = buildPayload();
      
      // Ensure label is always set and not empty
      if (!payload.label || !payload.label.trim()) {
        showError('Contract type name is required and cannot be empty');
        setIsSubmitting(false);
        return;
      }
      
      await MasterDataService.create(payload);
      success('Contract type created successfully!');
      navigate('/contracts/types/management');
    } catch (err) {
      console.error('Error creating contract type:', err);
      let message = 'Failed to create contract type. Please try again.';
      
      if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.message) {
        message = err.message;
      }
      
      // Check if it's a duplicate key error
      if (err.response?.status === 409 || message.toLowerCase().includes('duplicate') || message.toLowerCase().includes('already exists')) {
        message = `A contract type with this name already exists. Please use a different name.`;
      }
      
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBasicInformation = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Basic Information</h3>
        <p className="section-description">Contract type name, description, and basic details</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Contract Type Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., Fixed-Price Software Development"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Category</label>
          <select
            className="form-select"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
          >
            <option value="">Select category</option>
            <option value="Software Development">Software Development</option>
            <option value="Consulting">Consulting</option>
            <option value="Equipment">Equipment</option>
            <option value="Services">Services</option>
            <option value="Software">Software</option>
            <option value="Support">Support</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
          >
            <option value="">Select type</option>
            <option value="Fixed-Price">Fixed-Price</option>
            <option value="Time & Materials">Time & Materials</option>
            <option value="Lease">Lease</option>
            <option value="SLA">Service Level Agreement</option>
            <option value="Subscription">Subscription</option>
            <option value="Maintenance">Maintenance</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Describe the contract type, its purpose, and key characteristics..."
        />
      </div>
    </div>
  );

  const renderPricingModel = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Pricing Model Configuration</h3>
        <p className="section-description">Configure pricing structure and billing methods</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Pricing Model *</label>
          <select
            className="form-select"
            value={formData.pricingModel}
            onChange={(e) => handleInputChange('pricingModel', e.target.value)}
            required
          >
            <option value="Fixed-Price">Fixed-Price</option>
            <option value="Time & Materials">Time & Materials</option>
            <option value="Hybrid">Hybrid</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Currency</label>
          <select
            className="form-select"
            value={formData.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="INR">INR</option>
            <option value="CAD">CAD</option>
            <option value="AUD">AUD</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Billing Frequency</label>
          <select
            className="form-select"
            value={formData.billingFrequency}
            onChange={(e) => handleInputChange('billingFrequency', e.target.value)}
          >
            <option value="One-time">One-time</option>
            <option value="Monthly">Monthly</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Semi-Annual">Semi-Annual</option>
            <option value="Annual">Annual</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
        </div>
      </div>
      
      {formData.pricingModel === 'Fixed-Price' && (
        <>
          <div className="form-group">
            <label className="form-label">Base Amount</label>
            <div className="budget-input-group">
              <input
                type="number"
                className="form-control budget-amount"
                value={formData.baseAmount}
                onChange={(e) => handleInputChange('baseAmount', e.target.value)}
                placeholder="0"
              />
              <select className="form-select budget-currency">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="INR">INR</option>
              </select>
            </div>
          </div>
          
          {/* Fixed-Price Specific Configuration */}
          <div className="form-section">
            <h4 className="section-subtitle">Fixed-Price Configuration</h4>
            
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Change Management</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={formData.fixedPrice.changeManagement.enabled}
                    onChange={(e) => handleNestedInputChange('fixedPrice.changeManagement.enabled', e.target.checked)}
                  />
                  <label className="form-check-label">Enable Change Management</label>
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={formData.fixedPrice.changeManagement.approvalRequired}
                    onChange={(e) => handleNestedInputChange('fixedPrice.changeManagement.approvalRequired', e.target.checked)}
                  />
                  <label className="form-check-label">Require Approval for Changes</label>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Max Changes Allowed</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.fixedPrice.changeManagement.maxChanges}
                  onChange={(e) => handleNestedInputChange('fixedPrice.changeManagement.maxChanges', parseInt(e.target.value))}
                  placeholder="5"
                />
              </div>
            </div>
            
            {/* Scope Items */}
            <div className="form-group">
              <label className="form-label">Scope Items</label>
              <div className="dynamic-array">
                {formData.fixedPrice.scope.map((item, index) => (
                  <div key={index} className="dynamic-item">
                    <input
                      type="text"
                      className="form-control"
                      value={item}
                      onChange={(e) => handleArrayItemChange('fixedPrice.scope', index, e.target.value)}
                      placeholder="Enter scope item"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleArrayRemove('fixedPrice.scope', index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <div className="dynamic-add">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Add scope item"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleArrayAdd('fixedPrice.scope', e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={(e) => {
                      const input = e.target.previousElementSibling;
                      if (input && input.value) {
                        handleArrayAdd('fixedPrice.scope', input.value);
                        input.value = '';
                      }
                    }}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Deliverables */}
            <div className="form-group">
              <label className="form-label">Deliverables</label>
              <div className="dynamic-array">
                {formData.fixedPrice.deliverables.map((item, index) => (
                  <div key={index} className="dynamic-item">
                    <input
                      type="text"
                      className="form-control"
                      value={item}
                      onChange={(e) => handleArrayItemChange('fixedPrice.deliverables', index, e.target.value)}
                      placeholder="Enter deliverable"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleArrayRemove('fixedPrice.deliverables', index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <div className="dynamic-add">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Add deliverable"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleArrayAdd('fixedPrice.deliverables', e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={(e) => {
                      const input = e.target.previousElementSibling;
                      if (input && input.value) {
                        handleArrayAdd('fixedPrice.deliverables', input.value);
                        input.value = '';
                      }
                    }}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {formData.pricingModel === 'Time & Materials' && (
        <>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Hourly Rate</label>
              <div className="budget-input-group">
                <input
                  type="number"
                  className="form-control budget-amount"
                  value={formData.hourlyRate}
                  onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                  placeholder="0"
                />
                <select className="form-select budget-currency">
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Material Markup (%)</label>
              <input
                type="number"
                className="form-control"
                value={formData.materialMarkup}
                onChange={(e) => handleInputChange('materialMarkup', e.target.value)}
                placeholder="e.g., 15"
              />
            </div>
          </div>
          
          {/* Time & Materials Specific Configuration */}
          <div className="form-section">
            <h4 className="section-subtitle">Time & Materials Configuration</h4>
            
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Time Tracking</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={formData.timeMaterials.timeTracking.enabled}
                    onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking.enabled', e.target.checked)}
                  />
                  <label className="form-check-label">Enable Time Tracking</label>
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={formData.timeMaterials.timeTracking.required}
                    onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking.required', e.target.checked)}
                  />
                  <label className="form-check-label">Time Tracking Required</label>
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={formData.timeMaterials.timeTracking.overtimeAllowed}
                    onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking.overtimeAllowed', e.target.checked)}
                  />
                  <label className="form-check-label">Allow Overtime</label>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Max Hours Per Day</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.timeMaterials.timeTracking.maxHoursPerDay}
                  onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking.maxHoursPerDay', parseInt(e.target.value))}
                  placeholder="8"
                />
              </div>
            </div>
            
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Cost Tracking</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={formData.timeMaterials.costTracking.enabled}
                    onChange={(e) => handleNestedInputChange('timeMaterials.costTracking.enabled', e.target.checked)}
                  />
                  <label className="form-check-label">Enable Cost Tracking</label>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Cost Ceiling</label>
                <div className="budget-input-group">
                  <input
                    type="number"
                    className="form-control budget-amount"
                    value={formData.timeMaterials.costTracking.costCeiling}
                    onChange={(e) => handleNestedInputChange('timeMaterials.costTracking.costCeiling', parseFloat(e.target.value))}
                    placeholder="0"
                  />
                  <select className="form-select budget-currency">
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Hourly Rates by Role */}
            <div className="form-group">
              <label className="form-label">Hourly Rates by Role</label>
              <div className="dynamic-array">
                {formData.timeMaterials.hourlyRates.map((rate, index) => (
                  <div key={index} className="dynamic-item">
                    <input
                      type="text"
                      className="form-control"
                      value={rate.role}
                      onChange={(e) => handleNestedArrayItemChange('timeMaterials.hourlyRates', index, 'role', e.target.value)}
                      placeholder="Role (e.g., Senior Developer)"
                    />
                    <input
                      type="number"
                      className="form-control"
                      value={rate.rate}
                      onChange={(e) => handleNestedArrayItemChange('timeMaterials.hourlyRates', index, 'rate', parseFloat(e.target.value))}
                      placeholder="Rate"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleArrayRemove('timeMaterials.hourlyRates', index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <div className="dynamic-add">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleArrayAdd('timeMaterials.hourlyRates', { role: '', rate: 0 })}
                  >
                    <Plus size={16} /> Add Role Rate
                  </button>
                </div>
              </div>
            </div>
            
            {/* Material Costs */}
            <div className="form-group">
              <label className="form-label">Material Cost Categories</label>
              <div className="dynamic-array">
                {formData.timeMaterials.materialCosts.map((cost, index) => (
                  <div key={index} className="dynamic-item">
                    <input
                      type="text"
                      className="form-control"
                      value={cost.category}
                      onChange={(e) => handleNestedArrayItemChange('timeMaterials.materialCosts', index, 'category', e.target.value)}
                      placeholder="Category (e.g., Hardware, Software)"
                    />
                    <input
                      type="number"
                      className="form-control"
                      value={cost.markup}
                      onChange={(e) => handleNestedArrayItemChange('timeMaterials.materialCosts', index, 'markup', parseFloat(e.target.value))}
                      placeholder="Markup %"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleArrayRemove('timeMaterials.materialCosts', index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <div className="dynamic-add">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleArrayAdd('timeMaterials.materialCosts', { category: '', markup: 0 })}
                  >
                    <Plus size={16} /> Add Material Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderContractTerms = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Contract Terms</h3>
        <p className="section-description">Duration, renewal, and termination terms</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Default Duration</label>
          <input
            type="text"
            className="form-control"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            placeholder="e.g., 12 months, 3-6 months"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Notice Period (days)</label>
          <input
            type="number"
            className="form-control"
            value={formData.noticePeriod}
            onChange={(e) => handleInputChange('noticePeriod', e.target.value)}
            placeholder="30"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Auto Renewal</label>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="autoRenewal"
              checked={formData.autoRenewal}
              onChange={(e) => handleInputChange('autoRenewal', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="autoRenewal">
              Enable auto-renewal
            </label>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Approval Level</label>
          <select
            className="form-select"
            value={formData.approvalLevel}
            onChange={(e) => handleInputChange('approvalLevel', e.target.value)}
          >
            <option value="Manager">Manager</option>
            <option value="Director">Director</option>
            <option value="VP">VP</option>
            <option value="CEO">CEO</option>
            <option value="Board">Board</option>
          </select>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Renewal Terms</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.renewalTerms}
          onChange={(e) => handleInputChange('renewalTerms', e.target.value)}
          placeholder="Describe renewal terms and conditions..."
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Termination Clause</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.terminationClause}
          onChange={(e) => handleInputChange('terminationClause', e.target.value)}
          placeholder="Describe termination conditions and procedures..."
        />
      </div>
    </div>
  );

  const renderWorkflowTemplates = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Approval Workflow</h3>
        <p className="section-description">Define the approval process for this contract type</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Workflow Steps</label>
        <div className="workflow-steps-container">
          {formData.workflowSteps.map((step, index) => (
            <div key={index} className="workflow-step-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Step Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={step.name}
                    onChange={(e) => handleWorkflowStepChange(index, 'name', e.target.value)}
                    placeholder="e.g., Legal Review"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Approver</label>
                  <select
                    className="form-select"
                    value={step.approver}
                    onChange={(e) => handleWorkflowStepChange(index, 'approver', e.target.value)}
                  >
                    <option value="">Select approver</option>
                    <option value="legal-team">Legal Team</option>
                    <option value="finance-team">Finance Team</option>
                    <option value="manager">Manager</option>
                    <option value="director">Director</option>
                    <option value="ceo">CEO</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Time Limit (days)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={step.timeLimit}
                    onChange={(e) => handleWorkflowStepChange(index, 'timeLimit', e.target.value)}
                    placeholder="7"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Required</label>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`required-${index}`}
                      checked={step.required}
                      onChange={(e) => handleWorkflowStepChange(index, 'required', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`required-${index}`}>
                      Required step
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={step.description}
                  onChange={(e) => handleWorkflowStepChange(index, 'description', e.target.value)}
                  placeholder="Step description and requirements"
                />
              </div>
              
              <div className="workflow-step-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveWorkflowStep(index)}
                >
                  <X size={16} />
                  Remove Step
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-workflow-step">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddWorkflowStep}
            >
              <Plus size={16} />
              Add Workflow Step
            </button>
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Compliance Requirements</h3>
        <p className="section-description">Define compliance and review requirements</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Review Requirements</label>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="requiresLegalReview"
              checked={formData.requiresLegalReview}
              onChange={(e) => handleInputChange('requiresLegalReview', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="requiresLegalReview">
              Requires Legal Review
            </label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="requiresFinanceReview"
              checked={formData.requiresFinanceReview}
              onChange={(e) => handleInputChange('requiresFinanceReview', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="requiresFinanceReview">
              Requires Finance Review
            </label>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Compliance Requirements</label>
        {formData.category ? (
          <SearchableDropdown
            options={complianceTypes}
            value={formData.complianceRequirements}
            onChange={(selected) => {
              setFormData(prev => ({
                ...prev,
                complianceRequirements: selected
              }));
            }}
            placeholder={loadingComplianceTypes ? 'Loading compliance types...' : 'Search and select compliance requirements'}
            multiple={true}
            loading={loadingComplianceTypes}
            disabled={loadingComplianceTypes || !formData.category}
          />
        ) : (
          <div className="alert alert-info" style={{ margin: 0, padding: '12px' }}>
            Please select a contract category first to see available compliance requirements.
          </div>
        )}
        {formData.complianceRequirements.length > 0 && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#6c757d' }}>
            {formData.complianceRequirements.length} compliance requirement(s) selected
          </div>
        )}
      </div>
    </div>
  );

  const renderFeaturesIntegration = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Features & Capabilities</h3>
        <p className="section-description">Define features and capabilities for this contract type</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Contract Features</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add feature"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('features', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('features', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.features.map((feature, index) => (
              <div key={index} className="array-item">
                <span>{feature}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('features', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">System Integrations</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add integration"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('integrations', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('integrations', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.integrations.map((integration, index) => (
              <div key={index} className="array-item">
                <span>{integration}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('integrations', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Reporting Requirements</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add reporting requirement"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('reportingRequirements', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('reportingRequirements', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.reportingRequirements.map((requirement, index) => (
              <div key={index} className="array-item">
                <span>{requirement}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('reportingRequirements', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Advanced Settings</h3>
        <p className="section-description">Additional configuration and metadata</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Tags</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add tag"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('tags', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('tags', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.tags.map((tag, index) => (
              <div key={index} className="array-item">
                <span>{tag}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('tags', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Public Contract Type</label>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="isPublic"
              checked={formData.isPublic}
              onChange={(e) => handleInputChange('isPublic', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="isPublic">
              Make this contract type available to all users
            </label>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Allow Customization</label>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="allowCustomization"
              checked={formData.allowCustomization}
              onChange={(e) => handleInputChange('allowCustomization', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="allowCustomization">
              Allow users to customize this contract type
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInformation();
      case 'pricing':
        return (
          <div>
            {renderPricingModel()}
            {renderContractTerms()}
          </div>
        );
      case 'workflow':
        return renderWorkflowTemplates();
      case 'features':
        return renderFeaturesIntegration();
      case 'advanced':
        return renderAdvancedSettings();
      default:
        return renderBasicInformation();
    }
  };

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="project-create-container">
          <div className="page-title-section">
            <h1 className="page-title">Create Contract Type</h1>
            <p className="page-subtitle">
              Configure a new contract type with pricing models, workflows, and features
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="project-create-container">
        <form onSubmit={handleSubmit}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-tab-content">
            <div className="project-form-card">
              {renderTabContent()}
            </div>
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/contracts/types/management')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Creating Contract Type...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Create Contract Type
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateContractType;
